// ./src/components/features/ThemesTable/ThemesTable.tsx
import React, { useMemo } from "react";
import { Stack, Text } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
} from "@/components/generic";

import { Theme } from "@/utils/api/ThemeService";

export interface ThemesTableProps {
  /** Array of themes to display */
  themes: Theme[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditTheme?: (theme: Theme) => void;

  onDuplicateTheme?: (theme: Theme) => void;

  /** Callback when remove action is clicked */
  onRemoveTheme?: (theme: Theme) => void;

  /** Callback when view action is clicked */
  onViewTheme?: (theme: Theme) => void;

  /** ID of theme currently being removed */
  removingThemeId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ThemesTable: React.FC<ThemesTableProps> = ({
  themes,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditTheme,
  onDuplicateTheme,
  onRemoveTheme,
  removingThemeId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Theme>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Theme Name",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (value, record) => (
          <Stack gap={0}>
            <Text fw={500}>{value}</Text>
            {!!record.description && (
              <Text c="dimmed" size="sm" fw={500}>
                {record.description}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 140,
        minWidth: 140,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 230,
        minWidth: 230,
        align: "left",
        render: (_, record: any) => (
          <RecordActions
            context={record}
            //onViewClick={onViewTheme}
            onEditClick={
              record.rights?.includes("edit") ? onEditTheme : undefined
            }
            onRemoveClick={
              record.rights?.includes("remove") ? onRemoveTheme : undefined
            }
            onDuplicateClick={
              record.rights?.includes("duplicate")
                ? onDuplicateTheme
                : undefined
            }
            removeInProgress={removingThemeId === record.id}
          />
        ),
      },
    ],
    [onEditTheme, onDuplicateTheme, onRemoveTheme, removingThemeId]
  );

  return (
    <AppTable
      data={themes}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(theme) => theme?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
